import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiDownload, FiCopy, FiCheck, FiZap, FiMessageCircle } from 'react-icons/fi';

function AIAssistant() {
  const [copied, setCopied] = useState(false);

  const aiPromptTemplate = `# AVALW Extension Development Guide for AI Assistants

================================================================================
TECHNICAL SPECIFICATION DOCUMENT
AVALW Browser Extension Format - Developer Guide for AI-Assisted Development
Version 1.0 | Last Updated: 2024
================================================================================

## INTRODUCTION

This document provides comprehensive technical specifications for developing browser extensions using the AVALW (Advanced Virtual Application Loading Wrapper) format, designed specifically for Cronos Browser. This guide is intended to be provided to AI coding assistants to enable non-programmers to create functional browser extensions through AI-assisted development.

### Purpose of This Document

As a user who may not have programming experience, I am seeking assistance from an AI coding assistant to develop a browser extension. This document contains all necessary technical information, architectural details, API specifications, and development workflows required for the AI to generate production-ready extension code.

### Target Platform

- Browser: Cronos Browser
- Extension Format: .avalw (proprietary binary format)
- Runtime Environment: Browser-side JavaScript execution
- Development Platform: extension.avalw.com

## SECTION 1: AVALW EXTENSION FORMAT OVERVIEW

### 1.1 Format Specification

AVALW is a proprietary, compiled binary extension format that differs fundamentally from traditional browser extension formats:

COMPARISON WITH OTHER FORMATS:
- Chrome Extensions: Use .crx format (signed ZIP archives)
- Firefox Extensions: Use .xpi format (signed ZIP archives)
- AVALW Extensions: Use .avalw format (COMPILED BINARY packages)

KEY DIFFERENCES:
- Traditional formats are essentially ZIP archives containing source code
- AVALW files are compiled binaries with obfuscated code
- Source code is NOT directly accessible in deployed .avalw files
- Provides intellectual property protection for sensitive logic

### 1.2 Security and Compilation Architecture

The AVALW build system implements a multi-stage compilation process:

STAGE 1: SOURCE CODE VALIDATION
- Validates manifest.avalw JSON structure
- Checks for required fields and proper syntax
- Validates permission declarations
- Scans for potentially malicious code patterns

STAGE 2: CODE TRANSFORMATION
- JavaScript minification (whitespace removal, variable renaming)
- Code obfuscation (control flow flattening, string encoding)
- Dead code elimination
- Dependency tree optimization

STAGE 3: BINARY COMPILATION
- Proprietary binary format assembly
- Magic bytes header: 0x41 0x56 0x41 0x4C 0x57 (ASCII: "AVALW")
- Version header: 0x01 0x00 (Version 1.0)
- File structure serialization with metadata

STAGE 4: ENCRYPTION (Production)
- AES-256-CBC encryption of binary payload
- Integrity hash generation (SHA-256)
- Digital signature application (for marketplace distribution)

This compilation process ensures:
- Protection of payment processing logic
- Security for cryptocurrency wallet implementations
- Prevention of code tampering
- Intellectual property protection
- Resistance to reverse engineering

### 1.3 Why Binary Compilation Matters

For extensions handling sensitive operations such as:
- Payment processing and financial transactions
- Cryptocurrency wallet management
- Authentication and credential handling
- Proprietary algorithms and business logic

Binary compilation provides:
- Code obfuscation preventing easy decompilation
- Protection against intellectual property theft
- Prevention of unauthorized modifications
- Secure distribution through marketplace

## SECTION 2: EXTENSION ARCHITECTURE AND FILE STRUCTURE

### 2.1 Complete Extension File Hierarchy

An AVALW extension follows a structured file organization:

/my-extension/
├── manifest.avalw          # Extension configuration (REQUIRED)
├── background.js           # Background service worker (optional)
├── content.js              # Content script for web pages (optional)
├── popup.html              # Extension popup interface (optional)
├── popup.js                # Popup interface logic (optional)
├── styles.css              # Styling for popup/content (optional)
├── /assets/                # Asset directory (optional)
│   ├── icon-16.png        # 16x16 icon
│   ├── icon-48.png        # 48x48 icon
│   ├── icon-128.png       # 128x128 icon
│   └── [other assets]
└── /lib/                   # External libraries (optional)
    └── [library files]

### 2.2 manifest.avalw Specification

The manifest.avalw file is a JSON configuration file that defines extension metadata, permissions, and entry points.

COMPLETE MANIFEST SCHEMA:

{
  "name": "string",              // REQUIRED: Extension name (max 45 chars)
  "version": "string",           // REQUIRED: Semantic version (e.g., "1.0.0")
  "description": "string",       // REQUIRED: Extension description (max 132 chars)
  "author": "string",            // REQUIRED: Developer name or organization

  "permissions": ["string"],     // OPTIONAL: Array of permission strings
  "background": "string",        // OPTIONAL: Background script file path
  "content_scripts": ["string"], // OPTIONAL: Array of content script file paths
  "popup": "string",             // OPTIONAL: Popup HTML file path
  "icon": "string",              // OPTIONAL: Icon file path (recommend 128x128)

  "host_permissions": ["string"], // OPTIONAL: Host patterns for content scripts
  "web_accessible_resources": ["string"], // OPTIONAL: Files accessible from web pages
  "minimum_browser_version": "string"     // OPTIONAL: Minimum Cronos Browser version
}

MANIFEST EXAMPLE WITH ALL FIELDS:

{
  "name": "Advanced Page Analyzer",
  "version": "1.2.3",
  "description": "Analyzes web pages for SEO optimization and accessibility compliance",
  "author": "John Developer",
  "permissions": ["storage", "tabs", "notifications"],
  "background": "background.js",
  "content_scripts": ["content.js"],
  "popup": "popup.html",
  "icon": "assets/icon-128.png",
  "host_permissions": ["https://*/*", "http://*/*"],
  "web_accessible_resources": ["assets/styles.css"],
  "minimum_browser_version": "1.0.0"
}

FIELD VALIDATION RULES:

- name: Must be 1-45 characters, alphanumeric and spaces only
- version: Must follow semantic versioning (MAJOR.MINOR.PATCH)
- description: Must be 1-132 characters
- author: Must be 1-50 characters
- permissions: Each permission must be from allowed list (see Section 3)
- File paths: Must be relative to extension root, use forward slashes
- host_permissions: Must be valid match patterns (see Section 4.3)

## SECTION 3: RUNTIME ENVIRONMENT AND CAPABILITIES

### 3.1 Execution Context

CRITICAL UNDERSTANDING:
AVALW extensions execute ENTIRELY in the browser client-side environment. They do NOT run on servers and do NOT have access to server-side APIs provided by AVALW platform.

EXECUTION ENVIRONMENTS:

1. BACKGROUND SCRIPTS (background.js)
   - Run in isolated background context
   - Persist across browser sessions (service worker model)
   - No access to DOM of web pages
   - Can communicate with content scripts via messaging
   - Suitable for: Event handling, data processing, API calls

2. CONTENT SCRIPTS (content.js)
   - Inject into web pages matching host_permissions
   - Full access to DOM of host page
   - Isolated JavaScript context (cannot access page's JS variables)
   - Can communicate with background scripts via messaging
   - Suitable for: Page manipulation, data extraction, UI injection

3. POPUP SCRIPTS (popup.js)
   - Execute when extension icon clicked
   - Run in popup window context
   - Access to limited DOM (popup.html only)
   - Can communicate with background/content scripts
   - Suitable for: User interface, settings, quick actions

### 3.2 Available Web APIs

Extensions have access to standard browser Web APIs:

DOM MANIPULATION:
- document.querySelector() / querySelectorAll()
- document.createElement() / appendChild() / removeChild()
- element.innerHTML / textContent / classList / style
- MutationObserver for DOM change detection
- Event listeners (addEventListener)

DATA STORAGE:
- localStorage.setItem() / getItem() / removeItem()
- sessionStorage (session-scoped storage)
- IndexedDB (for large datasets, complex queries)
- Note: Storage is origin-isolated per extension

NETWORK COMMUNICATION:
- fetch() API for HTTP/HTTPS requests
- XMLHttpRequest (legacy, prefer fetch)
- WebSocket for real-time communication
- Note: Subject to CORS policies unless webRequest permission granted

USER INTERACTION APIs:
- Notification API (requires 'notifications' permission)
- Geolocation API (requires user permission)
- Clipboard API (requires user gesture)
- File API (for user-selected files only)

TIMING AND ASYNC:
- setTimeout() / setInterval() / clearTimeout() / clearInterval()
- Promises, async/await
- requestAnimationFrame() for smooth animations

### 3.3 Browser-Specific Features

Cronos Browser MAY provide additional features through window.cronos object:

if (window.cronos) {
  // Browser-specific functionality
  // Exact API to be documented by Cronos Browser team
  // Use feature detection before accessing
}

### 3.4 Restrictions and Limitations

Extensions CANNOT access:

- Server-side APIs provided by extension.avalw.com platform
- Direct database connections (MySQL, PostgreSQL, etc.)
- File system read/write (except user-selected files via File API)
- Native OS APIs
- Other extensions' storage or scripts
- Pages outside declared host_permissions

SECURITY RESTRICTIONS:
- Cannot use eval() in strict mode
- Cannot inject inline scripts with script-src unsafe-inline
- Cannot make requests to file:// URLs by default
- Cannot access chrome:// or other privileged URLs

## SECTION 4: PERMISSIONS SYSTEM

### 4.1 Available Permissions

Request ONLY permissions your extension genuinely needs. Users can see all requested permissions before installation.

PERMISSION: "storage"
- Grants access to localStorage and sessionStorage APIs
- Allows persistent data storage across browser sessions
- Data limit: Typically 5-10MB per extension
- Use case: Save user preferences, cache data, store state

PERMISSION: "tabs"
- Access to browser tab information
- Query open tabs, get tab URLs and titles
- Listen to tab creation/removal/update events
- DOES NOT grant access to tab content (use content_scripts)
- Use case: Tab management, context awareness

PERMISSION: "cookies"
- Read and modify cookies for permitted hosts
- Set, get, remove cookies programmatically
- Listen to cookie changes
- Requires host_permissions for target domains
- Use case: Session management, cross-domain auth

PERMISSION: "notifications"
- Display desktop notifications to user
- Create rich notifications with icons, buttons
- Handle notification click events
- Subject to OS notification permission
- Use case: Alerts, updates, reminders

PERMISSION: "webRequest"
- Intercept and modify HTTP requests
- Block or redirect requests
- Modify request/response headers
- POWERFUL: Use with caution, can break websites
- Use case: Content filtering, ad blocking, request logging

PERMISSION: "contextMenus"
- Add items to browser right-click context menu
- Create menu hierarchies
- Respond to menu item clicks with callbacks
- Use case: Quick actions, page interaction shortcuts

### 4.2 Permission Declaration Example

{
  "name": "Example Extension",
  "version": "1.0.0",
  "description": "Demonstrates permission usage",
  "author": "Developer",
  "permissions": [
    "storage",
    "tabs",
    "notifications"
  ],
  "host_permissions": [
    "https://example.com/*",
    "https://api.example.com/*"
  ]
}

### 4.3 Host Permissions (Match Patterns)

Host permissions define which websites content scripts can access.

MATCH PATTERN SYNTAX:
<scheme>://<host><path>

SCHEME:
- http: HTTP protocol only
- https: HTTPS protocol only
- *: Both HTTP and HTTPS
- file: Local files (rarely granted)

HOST:
- example.com: Exact domain
- *.example.com: All subdomains
- *: All domains (use sparingly)

PATH:
- /: Root only
- /*: All paths
- /api/*: Specific path prefix

EXAMPLES:
- https://example.com/*: All pages on example.com (HTTPS)
- https://*/*: All HTTPS pages on all domains
- *://*.google.com/*: All pages on Google and subdomains (HTTP/HTTPS)

## SECTION 5: DEVELOPMENT WORKFLOW

### 5.1 Complete Development Process

STEP 1: REQUIREMENTS GATHERING
The AI assistant will ask clarifying questions about:
- Extension purpose and core functionality
- Target websites or general browser usage
- UI requirements (popup, page modifications, etc.)
- Data storage needs
- External API integrations
- User interaction patterns

STEP 2: ARCHITECTURE DESIGN
The AI will determine:
- Required extension components (background/content/popup)
- Necessary permissions
- File structure
- Communication patterns between components
- Data flow and storage strategy

STEP 3: CODE GENERATION
The AI will generate complete source files:

FILE: manifest.avalw
- Complete JSON configuration
- All required and optional fields
- Appropriate permissions
- Correct file references

FILE: background.js (if needed)
- Event listeners and handlers
- Data processing logic
- API communication
- Message handling from content/popup scripts

FILE: content.js (if needed)
- DOM manipulation logic
- Page interaction handlers
- Data extraction code
- Message passing to background script

FILE: popup.html + popup.js + styles.css (if needed)
- User interface HTML structure
- Interactive JavaScript logic
- Styled CSS for professional appearance

FILE: Additional assets
- Icon files (16x16, 48x48, 128x128 PNG)
- Other required resources

STEP 4: DEVELOPER UPLOADS TO PLATFORM
Navigate to extension.avalw.com:

1. Create developer account (if first time)
2. Log in to developer dashboard
3. Click "New Extension" or "Upload Extension"
4. Upload all source files generated by AI
5. Fill in extension metadata (name, description, category)
6. Review permissions and file manifest
7. Click "Build Extension" button

STEP 5: BUILD SYSTEM COMPILATION
The platform automatically:

1. Validates manifest.avalw structure
2. Checks all file references exist
3. Scans code for security vulnerabilities
4. Minifies JavaScript files
5. Obfuscates code for IP protection
6. Compiles into binary .avalw format
7. Generates SHA-256 integrity hash
8. Signs with platform certificate
9. Makes .avalw file available for download

Build time: Typically 10-30 seconds depending on extension size

STEP 6: LOCAL TESTING
Test the compiled extension:

1. Download the compiled .avalw file from dashboard
2. Open Cronos Browser
3. Navigate to Settings > Extensions (or cronos://extensions/)
4. Toggle "Developer Mode" to ON
5. Click "Load Extension" button
6. Select the downloaded .avalw file
7. Extension installs and activates
8. Test all functionality thoroughly:
   - Verify popup appears and functions correctly
   - Check content script modifications on target pages
   - Test background script event handling
   - Verify data storage and retrieval
   - Test all user interactions
   - Check browser console for errors

STEP 7: ITERATION (if needed)
If issues found during testing:

1. Document specific problems, error messages, unexpected behavior
2. Provide detailed feedback to AI assistant
3. AI generates updated code fixes
4. Re-upload source files to platform
5. Rebuild extension
6. Test again
7. Repeat until all issues resolved

STEP 8: MARKETPLACE PUBLICATION
Once fully tested and working:

1. From developer dashboard, click "Publish to Marketplace"
2. Add extension screenshots (recommended 4-6 images)
3. Write comprehensive description
4. Set pricing (free or paid)
5. Choose category and tags
6. Submit for marketplace review
7. Review typically completes in 24-48 hours
8. Extension goes live on marketplace if approved

### 5.2 Build System Technical Details

The AVALW build system performs several security and optimization steps:

VALIDATION:
- JSON schema validation for manifest
- Required field presence check
- Semantic version format validation
- Permission whitelist verification
- File path validation and existence check
- Host permission pattern syntax check

CODE ANALYSIS:
- Static code analysis for common vulnerabilities
- Detection of eval() usage
- Inline script injection detection
- Sensitive data exposure check
- CORS misconfiguration detection

TRANSFORMATION:
- Whitespace and comment removal
- Variable name minification
- Function inlining for performance
- Dead code elimination
- Constant folding and propagation

OBFUSCATION:
- Control flow flattening
- String encryption and encoding
- Number literal transformation
- Boolean expression complexification
- Anti-debugging techniques

BINARY ASSEMBLY:
- Magic byte header: 0x41564C4157 ("AVALW")
- Version header: 0x0100
- File count and metadata table
- Compressed file data
- Integrity checksum (SHA-256)
- Platform signature (RSA-2048)

## SECTION 6: COMPLETE CODE EXAMPLES

### 6.1 Example Extension: Email Highlighter

PURPOSE: Highlights email addresses on any webpage and adds copy-to-clipboard functionality

FILE: manifest.avalw
{
  "name": "Email Highlighter",
  "version": "1.0.0",
  "description": "Highlights email addresses and enables quick copying",
  "author": "Developer Name",
  "permissions": ["storage"],
  "content_scripts": ["content.js"],
  "host_permissions": ["https://*/*", "http://*/*"],
  "icon": "assets/icon-128.png"
}

FILE: content.js
// Email Highlighter - Content Script
(function() {
  'use strict';

  // Email regex pattern
  const EMAIL_REGEX = /\\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Z|a-z]{2,}\\b/g;

  // Settings storage key
  const SETTINGS_KEY = 'email_highlighter_settings';

  // Load user settings
  async function loadSettings() {
    const stored = localStorage.getItem(SETTINGS_KEY);
    return stored ? JSON.parse(stored) : {
      highlightColor: '#ffeb3b',
      enabled: true
    };
  }

  // Find and highlight email addresses
  function highlightEmails() {
    loadSettings().then(settings => {
      if (!settings.enabled) return;

      // Get all text nodes
      const walker = document.createTreeWalker(
        document.body,
        NodeFilter.SHOW_TEXT,
        null,
        false
      );

      const textNodes = [];
      let node;
      while (node = walker.nextNode()) {
        if (EMAIL_REGEX.test(node.nodeValue)) {
          textNodes.push(node);
        }
      }

      // Highlight each email
      textNodes.forEach(textNode => {
        const parent = textNode.parentNode;
        const fragment = document.createDocumentFragment();
        let lastIndex = 0;
        let match;

        EMAIL_REGEX.lastIndex = 0; // Reset regex
        const text = textNode.nodeValue;

        while ((match = EMAIL_REGEX.exec(text)) !== null) {
          // Add text before email
          if (match.index > lastIndex) {
            fragment.appendChild(
              document.createTextNode(text.slice(lastIndex, match.index))
            );
          }

          // Create highlighted email span
          const span = document.createElement('span');
          span.textContent = match[0];
          span.style.backgroundColor = settings.highlightColor;
          span.style.padding = '2px 4px';
          span.style.borderRadius = '3px';
          span.style.cursor = 'pointer';
          span.title = 'Click to copy';
          span.dataset.email = match[0];

          // Add click handler
          span.addEventListener('click', function(e) {
            e.preventDefault();
            copyToClipboard(this.dataset.email);
          });

          fragment.appendChild(span);
          lastIndex = EMAIL_REGEX.lastIndex;
        }

        // Add remaining text
        if (lastIndex < text.length) {
          fragment.appendChild(
            document.createTextNode(text.slice(lastIndex))
          );
        }

        parent.replaceChild(fragment, textNode);
      });
    });
  }

  // Copy email to clipboard
  function copyToClipboard(email) {
    navigator.clipboard.writeText(email).then(() => {
      showNotification('Email copied: ' + email);
    }).catch(err => {
      console.error('Copy failed:', err);
    });
  }

  // Show notification
  function showNotification(message) {
    const notification = document.createElement('div');
    notification.textContent = message;
    notification.style.cssText = \`
      position: fixed;
      top: 20px;
      right: 20px;
      background: #323232;
      color: white;
      padding: 12px 20px;
      border-radius: 4px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      z-index: 999999;
      font-family: Arial, sans-serif;
      font-size: 14px;
    \`;

    document.body.appendChild(notification);

    setTimeout(() => {
      notification.style.transition = 'opacity 0.3s';
      notification.style.opacity = '0';
      setTimeout(() => notification.remove(), 300);
    }, 2000);
  }

  // Run when page loads
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', highlightEmails);
  } else {
    highlightEmails();
  }

  // Observe DOM changes for dynamically loaded content
  const observer = new MutationObserver(highlightEmails);
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
})();

### 6.2 Example Extension: Tab Counter with Popup

PURPOSE: Displays count of open tabs and provides tab management

FILE: manifest.avalw
{
  "name": "Tab Counter",
  "version": "1.0.0",
  "description": "Counts open tabs and provides tab management",
  "author": "Developer Name",
  "permissions": ["tabs", "storage"],
  "background": "background.js",
  "popup": "popup.html",
  "icon": "assets/icon-128.png"
}

FILE: background.js
// Background script - Tab management
let tabCount = 0;

// Update tab count
function updateTabCount() {
  // Note: Actual tabs API may differ in Cronos Browser
  // This is示例 pseudocode
  if (window.cronos && window.cronos.tabs) {
    window.cronos.tabs.query({}, (tabs) => {
      tabCount = tabs.length;
      saveCount();
    });
  }
}

// Save count to storage
function saveCount() {
  localStorage.setItem('tab_counter_count', tabCount.toString());
}

// Initialize
updateTabCount();

// Listen for tab changes (browser-specific)
// Actual implementation depends on Cronos Browser API

FILE: popup.html
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Tab Counter</title>
  <link rel="stylesheet" href="styles.css">
</head>
<body>
  <div class="container">
    <h1>Tab Counter</h1>
    <div class="tab-count">
      <div class="count-number" id="countDisplay">0</div>
      <div class="count-label">Open Tabs</div>
    </div>
    <div class="actions">
      <button id="refreshBtn" class="btn btn-primary">Refresh Count</button>
      <button id="closeAllBtn" class="btn btn-danger">Close All Tabs</button>
    </div>
    <div class="stats">
      <p>Last updated: <span id="lastUpdate">Never</span></p>
    </div>
  </div>
  <script src="popup.js"></script>
</body>
</html>

FILE: popup.js
// Popup script
document.addEventListener('DOMContentLoaded', function() {
  const countDisplay = document.getElementById('countDisplay');
  const lastUpdate = document.getElementById('lastUpdate');
  const refreshBtn = document.getElementById('refreshBtn');
  const closeAllBtn = document.getElementById('closeAllBtn');

  // Load and display count
  function loadCount() {
    const count = localStorage.getItem('tab_counter_count') || '0';
    countDisplay.textContent = count;
    lastUpdate.textContent = new Date().toLocaleTimeString();
  }

  // Initial load
  loadCount();

  // Refresh button
  refreshBtn.addEventListener('click', function() {
    // Send message to background script to update count
    loadCount();
  });

  // Close all tabs (with confirmation)
  closeAllBtn.addEventListener('click', function() {
    if (confirm('Are you sure you want to close all tabs?')) {
      // Browser-specific implementation
      alert('This feature requires browser-specific API implementation');
    }
  });
});

FILE: styles.css
body {
  width: 300px;
  margin: 0;
  padding: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
  background: #f5f5f5;
}

.container {
  padding: 20px;
}

h1 {
  margin: 0 0 20px 0;
  font-size: 24px;
  color: #333;
  text-align: center;
}

.tab-count {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 30px;
  text-align: center;
  margin-bottom: 20px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
}

.count-number {
  font-size: 48px;
  font-weight: bold;
  color: white;
  margin-bottom: 8px;
}

.count-label {
  font-size: 14px;
  color: rgba(255,255,255,0.9);
  text-transform: uppercase;
  letter-spacing: 1px;
}

.actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
}

.btn {
  padding: 10px 16px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover {
  background: #5568d3;
}

.btn-danger {
  background: #f56565;
  color: white;
}

.btn-danger:hover {
  background: #e53e3e;
}

.stats {
  text-align: center;
  font-size: 12px;
  color: #666;
}

### 6.3 Example Extension: API Data Fetcher

PURPOSE: Fetches data from external API and displays in popup

FILE: manifest.avalw
{
  "name": "Weather Dashboard",
  "version": "1.0.0",
  "description": "Displays current weather from external API",
  "author": "Developer Name",
  "permissions": ["storage"],
  "background": "background.js",
  "popup": "popup.html",
  "icon": "assets/icon-128.png"
}

FILE: background.js
// Background script - Fetch weather data
const API_URL = 'https://api.openweathermap.org/data/2.5/weather';
const API_KEY = 'YOUR_API_KEY_HERE'; // User must provide their own key
const CACHE_KEY = 'weather_data_cache';
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

async function fetchWeather(city) {
  try {
    const response = await fetch(
      \`\${API_URL}?q=\${encodeURIComponent(city)}&appid=\${API_KEY}&units=metric\`
    );

    if (!response.ok) {
      throw new Error(\`HTTP error! status: \${response.status}\`);
    }

    const data = await response.json();

    // Cache the data
    const cacheData = {
      data: data,
      timestamp: Date.now(),
      city: city
    };

    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));

    return data;
  } catch (error) {
    console.error('Weather fetch error:', error);
    throw error;
  }
}

// Get cached data if still valid
function getCachedWeather() {
  const cached = localStorage.getItem(CACHE_KEY);
  if (!cached) return null;

  const cacheData = JSON.parse(cached);
  const age = Date.now() - cacheData.timestamp;

  if (age < CACHE_DURATION) {
    return cacheData.data;
  }

  return null;
}

// Message listener for popup requests
// Implementation depends on browser messaging API

## SECTION 7: SECURITY AND BEST PRACTICES

### 7.1 Security Considerations

PRINCIPLE 1: LEAST PRIVILEGE
- Request ONLY permissions absolutely necessary for functionality
- Avoid "tabs" permission if you don't need tab information
- Avoid "webRequest" unless intercepting requests is core feature
- Users scrutinize permission requests before installation

PRINCIPLE 2: DATA PROTECTION
- NEVER store passwords, API keys, or tokens in plain text
- Use browser's credential management when available
- Encrypt sensitive data before storing in localStorage
- Clear sensitive data when no longer needed
- Use HTTPS for all external API communications

PRINCIPLE 3: INPUT VALIDATION
- Sanitize all user input before DOM injection
- Validate data from external APIs
- Use textContent instead of innerHTML when possible
- Escape HTML entities in user-generated content
- Prevent XSS (Cross-Site Scripting) attacks

PRINCIPLE 4: CODE SECURITY
- Avoid eval() and Function() constructor
- No inline event handlers in HTML
- Use Content Security Policy headers
- Validate all message origins in message listeners
- Never execute code from untrusted sources

### 7.2 Performance Best Practices

DOM MANIPULATION:
- Minimize reflows and repaints
- Batch DOM updates using DocumentFragment
- Use requestAnimationFrame for animations
- Debounce scroll and resize event handlers
- Remove event listeners when elements are destroyed

MEMORY MANAGEMENT:
- Clear intervals and timers when not needed
- Remove event listeners on cleanup
- Avoid memory leaks from closures
- Use WeakMap for object associations
- Monitor memory usage in DevTools

STORAGE OPTIMIZATION:
- Compress large data before storing
- Use IndexedDB for datasets > 5MB
- Clean up old/unused cached data
- Implement storage quota management
- Handle QuotaExceededError gracefully

NETWORK EFFICIENCY:
- Cache API responses appropriately
- Implement request debouncing/throttling
- Use HTTP caching headers
- Minimize payload sizes
- Handle network errors gracefully

### 7.3 User Experience Best Practices

RESPONSIVENESS:
- Provide immediate visual feedback
- Show loading states for async operations
- Use progressive enhancement
- Handle errors gracefully with user-friendly messages
- Test on various screen sizes and resolutions

ACCESSIBILITY:
- Use semantic HTML elements
- Provide keyboard navigation
- Include ARIA labels where appropriate
- Ensure sufficient color contrast
- Support screen readers

PRIVACY:
- Be transparent about data collection
- Provide opt-out mechanisms
- Don't track users without explicit consent
- Anonymize analytics data
- Comply with GDPR/privacy regulations

### 7.4 Code Quality

MAINTAINABILITY:
- Write clear, descriptive variable and function names
- Add comments explaining complex logic
- Keep functions small and single-purpose
- Use consistent code formatting
- Follow JavaScript best practices (ES6+)

ERROR HANDLING:
- Wrap risky operations in try-catch blocks
- Log errors for debugging (but not sensitive data)
- Provide fallback behavior
- Handle promise rejections
- Test edge cases thoroughly

TESTING:
- Test on fresh browser profile
- Test with slow network conditions
- Test error scenarios (API failures, etc.)
- Test with various user inputs
- Test across different operating systems

## SECTION 8: AI ASSISTANT INSTRUCTIONS

### 8.1 Your Role as AI Assistant

As an AI coding assistant, you are responsible for:

REQUIREMENTS ANALYSIS:
1. Ask detailed clarifying questions about the extension's purpose
2. Identify all required functionality and features
3. Determine necessary permissions and file structure
4. Warn about technical limitations or impossibilities
5. Suggest improvements or alternative approaches

CODE GENERATION:
1. Generate COMPLETE, working source code for all files
2. Include comprehensive inline comments
3. Follow security best practices
4. Implement proper error handling
5. Write clean, maintainable code using modern JavaScript (ES6+)
6. Ensure code is production-ready, not prototype quality

DOCUMENTATION:
1. Explain what each file does and how they interact
2. Document any external dependencies or API requirements
3. Provide testing instructions
4. Explain how to customize or extend the extension
5. List any limitations or known issues

ITERATION SUPPORT:
1. Debug issues reported during testing
2. Add new features based on user feedback
3. Optimize performance if needed
4. Fix bugs promptly with explanations
5. Refactor code for better structure when appropriate

### 8.2 File Generation Guidelines

When generating extension code:

MANIFEST.AVALW:
- Include ALL required fields (name, version, description, author)
- Request ONLY necessary permissions
- Use correct file paths
- Follow semantic versioning
- Validate JSON syntax

JAVASCRIPT FILES:
- Use strict mode ('use strict')
- Implement proper error handling (try-catch)
- Add clear comments for complex logic
- Use modern ES6+ syntax (const/let, arrow functions, async/await)
- Avoid global namespace pollution (use IIFE or modules)

HTML FILES:
- Use semantic HTML5 elements
- Include proper DOCTYPE and meta tags
- Link stylesheets and scripts correctly
- Ensure accessibility (ARIA labels, semantic structure)
- Use external CSS files, not inline styles

CSS FILES:
- Use modern CSS (flexbox, grid)
- Implement responsive design
- Follow BEM or consistent naming convention
- Include vendor prefixes where necessary
- Optimize for performance (avoid expensive selectors)

### 8.3 USER'S EXTENSION IDEA

[USER WILL DESCRIBE THEIR EXTENSION IDEA HERE]

After reading the user's idea, you should:

1. ACKNOWLEDGE UNDERSTANDING
Respond with confirmation that you understand the AVALW format and architecture

2. ASK CLARIFYING QUESTIONS
Examples:
- "Should this work on all websites or specific domains?"
- "Do you want a popup interface or just background functionality?"
- "Should data persist across browser sessions?"
- "Do you need to fetch data from external APIs? If so, which ones?"
- "What should happen if [edge case scenario]?"

3. PROPOSE ARCHITECTURE
Describe which files you'll create and why:
- "I'll create a content script to modify the page..."
- "A background script will handle API requests..."
- "The popup will display the results..."

4. GENERATE COMPLETE CODE
Provide ALLCODE files with explanations

5. PROVIDE TESTING INSTRUCTIONS
- Exact steps to test each feature
- Expected behavior
- Common issues to watch for

## SECTION 9: VERIFICATION CHECKLIST

Before beginning development, confirm understanding of these critical points:

ARCHITECTURE UNDERSTANDING:
[ ] AVALW extensions run ENTIRELY in the browser client-side
[ ] Extensions do NOT have access to server-side AVALW platform APIs
[ ] Extensions can ONLY use standard Web APIs and browser features
[ ] Source code is compiled into BINARY format, not distributed as-is

FILE FORMAT:
[ ] Configuration file is named "manifest.avalw" (NOT manifest.json)
[ ] Final output is .avalw file (NOT .crx or .zip)
[ ] .avalw files are COMPILED BINARIES with obfuscated code
[ ] Source code is NOT accessible in deployed extensions

BUILD PROCESS:
[ ] Developer uploads SOURCE FILES to extension.avalw.com
[ ] Platform COMPILES sources into binary .avalw format
[ ] Developer TESTS compiled .avalw in Cronos Browser
[ ] After testing, developer PUBLISHES to marketplace
[ ] Review process takes 24-48 hours

CAPABILITIES:
[ ] Extensions can manipulate DOM of web pages
[ ] Extensions can store data in localStorage/IndexedDB
[ ] Extensions can make HTTP requests to external APIs
[ ] Extensions CANNOT access databases directly
[ ] Extensions CANNOT access file system (except user-selected files)

PERMISSIONS:
[ ] Only request permissions actually needed
[ ] Users see all permissions before installing
[ ] "storage" for localStorage access
[ ] "tabs" for tab information (if supported)
[ ] "notifications" for desktop notifications
[ ] "webRequest" for request interception (use sparingly)

## SECTION 10: READY TO START

If you (AI assistant) understand all the above specifications, please respond with:

"I understand the AVALW extension format completely. Let's build your extension!

To get started, please describe:
1. What should your extension do?
2. Which websites should it work on (all sites, or specific domains)?
3. Do you want a popup interface when clicking the extension icon?
4. Should it modify web pages, run in the background, or both?
5. Do you need to store any user data or preferences?

Once you provide these details, I'll generate complete, production-ready code for your AVALW extension."

THIS CONFIRMS:
- You understand AVALW is a proprietary binary format
- You know extensions run client-side in the browser
- You will use manifest.avalw (not manifest.json)
- You understand the compile-test-publish workflow
- You will generate complete, secure, well-documented code
- You will ask clarifying questions before coding
- You will provide testing and deployment instructions

## Compatible AI Assistants

This guide works with any AI assistant that has coding capabilities:
- **Claude** (Anthropic) - https://claude.ai
- **ChatGPT** (OpenAI) - https://chat.openai.com
- **Gemini** (Google) - https://gemini.google.com
- **GitHub Copilot** - Integrated in your IDE
- Or any other AI with code generation capabilities

Simply copy this entire guide and paste it into your preferred AI assistant to get started!

---

**Ready to build your extension? Copy this guide and paste it into your AI assistant!**
`;

  const handleDownload = () => {
    const blob = new Blob([aiPromptTemplate], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'avalw-ai-assistant-guide.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(aiPromptTemplate);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-white py-12 px-6">
      <div className="container mx-auto max-w-5xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 bg-gray-100 px-4 py-2 rounded-full mb-4">
            <FiZap className="text-gray-900" />
            <span className="text-sm font-medium text-gray-900">No Programming Required</span>
          </div>

          <h1 className="text-5xl font-bold mb-4 text-gray-900">
            Build Extensions with AI
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            No coding skills? No problem! Use AI assistants to build powerful AVALW extensions. Just describe your idea, and let AI generate the code for you.
          </p>
        </motion.div>

        {/* How it Works */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-gray-50 p-6 rounded-2xl border-2 border-gray-200">
            <div className="w-12 h-12 bg-gray-900 text-white rounded-full flex items-center justify-center text-xl font-bold mb-4">1</div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Download Guide</h3>
            <p className="text-gray-600 text-sm">Download or copy our comprehensive AI assistant guide that explains everything about AVALW extensions.</p>
          </div>

          <div className="bg-gray-50 p-6 rounded-2xl border-2 border-gray-200">
            <div className="w-12 h-12 bg-gray-900 text-white rounded-full flex items-center justify-center text-xl font-bold mb-4">2</div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Chat with AI</h3>
            <p className="text-gray-600 text-sm">Paste the guide into Claude.ai or your preferred AI assistant and describe your extension idea.</p>
          </div>

          <div className="bg-gray-50 p-6 rounded-2xl border-2 border-gray-200">
            <div className="w-12 h-12 bg-gray-900 text-white rounded-full flex items-center justify-center text-xl font-bold mb-4">3</div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Build & Publish</h3>
            <p className="text-gray-600 text-sm">Upload the AI-generated code to our platform, build it, test it, and publish to the marketplace!</p>
          </div>
        </div>

        {/* Main CTA */}
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-12 text-center mb-12">
          <h2 className="text-3xl font-bold text-white mb-4">
            Complete AI Assistant Guide
          </h2>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
            This guide contains everything an AI needs to help you build an AVALW extension:
            technical specs, code examples, best practices, and verification questions.
          </p>

          <div className="flex gap-4 justify-center flex-wrap">
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-8 py-4 bg-white text-gray-900 rounded-xl font-bold hover:bg-gray-100 transition-all shadow-lg"
            >
              <FiDownload className="text-xl" />
              Download Guide (.txt)
            </button>

            <button
              onClick={handleCopy}
              className="flex items-center gap-2 px-8 py-4 bg-gray-700 text-white rounded-xl font-bold hover:bg-gray-600 transition-all"
            >
              {copied ? (
                <>
                  <FiCheck className="text-xl" />
                  Copied!
                </>
              ) : (
                <>
                  <FiCopy className="text-xl" />
                  Copy to Clipboard
                </>
              )}
            </button>
          </div>
        </div>

        {/* Compatible AI Assistants */}
        <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 mb-12">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-gray-900 rounded-full flex items-center justify-center flex-shrink-0">
              <FiMessageCircle className="text-white text-2xl" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Works with Any AI Assistant
              </h3>
              <p className="text-gray-600 mb-4">
                This guide is compatible with any AI assistant that can generate code. Simply copy the guide and paste it into your preferred AI tool:
              </p>
              <div className="flex flex-wrap gap-3">
                <div className="px-4 py-2 bg-gray-100 rounded-lg text-gray-900 font-medium">Claude (Anthropic)</div>
                <div className="px-4 py-2 bg-gray-100 rounded-lg text-gray-900 font-medium">ChatGPT (OpenAI)</div>
                <div className="px-4 py-2 bg-gray-100 rounded-lg text-gray-900 font-medium">Gemini (Google)</div>
                <div className="px-4 py-2 bg-gray-100 rounded-lg text-gray-900 font-medium">GitHub Copilot</div>
                <div className="px-4 py-2 bg-gray-100 rounded-lg text-gray-900 font-medium">Or any other AI</div>
              </div>
            </div>
          </div>
        </div>

        {/* What's Included */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">What's in the Guide?</h2>

          <div className="grid md:grid-cols-2 gap-4">
            {[
              'Complete AVALW extension format explanation',
              'File structure and manifest.avalw details',
              'Available permissions and capabilities',
              'Code examples for common features',
              'Build and deployment workflow',
              'Security best practices',
              'Testing guidelines',
              'Verification questions for the AI'
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="w-6 h-6 bg-gray-900 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm">
                  ✓
                </div>
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Example Workflow */}
        <div className="bg-gray-50 rounded-2xl p-8 border-2 border-gray-200">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Example Workflow</h3>
          <div className="space-y-4 text-gray-700">
            <div className="flex gap-4">
              <span className="font-bold text-gray-900">You:</span>
              <span>"I want an extension that highlights all prices on Amazon in green."</span>
            </div>
            <div className="flex gap-4">
              <span className="font-bold text-gray-900">Claude:</span>
              <span>"I understand! I'll create a content script that finds price elements and highlights them. Here's the code..."</span>
            </div>
            <div className="flex gap-4">
              <span className="font-bold text-gray-900">You:</span>
              <span>[Upload code to AVALW → Build → Test → Publish]</span>
            </div>
            <div className="flex gap-4">
              <span className="font-bold text-gray-900">Result:</span>
              <span className="text-green-600 font-semibold">✓ Extension live on marketplace!</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AIAssistant;
